import { supabase } from '../config/supabase';

/**
 * Upload an image file to Supabase Storage
 * @param {File} file - The image file to upload
 * @param {string} vendorId - Optional vendor ID for organizing files
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const uploadImageToSupabase = async (file, vendorId = null) => {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      return { success: false, error: 'File must be an image' };
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { success: false, error: 'Image size must be less than 5MB' };
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = vendorId ? `vendors/${vendorId}/${fileName}` : `vendors/${fileName}`;

    // Upload to Supabase Storage
    const { error } = await supabase.storage
      .from('vendr-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Upload error:', error);
      return { success: false, error: error.message };
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('vendr-images')
      .getPublicUrl(filePath);

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error('Image upload error:', error);
    return { success: false, error: error.message || 'Failed to upload image' };
  }
};

/**
 * Delete an image from Supabase Storage
 * @param {string} imageUrl - The public URL of the image to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteImageFromSupabase = async (imageUrl) => {
  try {
    // Extract file path from URL
    const urlParts = imageUrl.split('/vendr-images/');
    if (urlParts.length < 2) {
      return { success: false, error: 'Invalid image URL' };
    }

    const filePath = urlParts[1];

    const { error } = await supabase.storage
      .from('vendr-images')
      .remove([filePath]);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

